import { useSyncExternalStore } from "react";

/**
 * Light / dark. The choice is per browser (localStorage); "system" follows
 * the OS and changes with it. The page is themed by `data-theme` on <html>:
 * `notion` or `notion-dark` — the daisyUI theme AND the token overrides in
 * styles.css key on it. index.html applies the stored choice before the
 * first paint (same key, same names), so a dark page never flashes white.
 */
export type ThemeChoice = "light" | "dark" | "system";
export type Theme = "light" | "dark";

const KEY = "seagames.notion.theme";
const DARK = "(prefers-color-scheme: dark)";
const listeners = new Set<() => void>();

function readChoice(): ThemeChoice {
	try {
		const v = localStorage.getItem(KEY);
		return v === "light" || v === "dark" ? v : "system";
	} catch {
		return "system";
	}
}

let choice: ThemeChoice = readChoice();

const systemDark = () => typeof matchMedia === "function" && matchMedia(DARK).matches;
const resolve = (c: ThemeChoice): Theme => (c === "system" ? (systemDark() ? "dark" : "light") : c);

function apply() {
	const theme = resolve(choice);
	const root = document.documentElement;
	root.dataset["theme"] = theme === "dark" ? "notion-dark" : "notion";
	root.style.colorScheme = theme;
	// index.html paints <body> from this before the stylesheet loads; keep it in step.
	root.style.setProperty("--wn-boot-bg", theme === "dark" ? "#191b22" : "#ffffff");
	document.querySelector('meta[name="theme-color"]')?.setAttribute("content", theme === "dark" ? "#191b22" : "#012c8e");
	for (const l of listeners) l();
}

if (typeof matchMedia === "function") {
	matchMedia(DARK).addEventListener("change", () => {
		if (choice === "system") apply();
	});
}

// Paper is white: a print or PDF (the menu's export, or Ctrl+P) always uses the light theme, then puts the choice back.
if (typeof window !== "undefined") {
	window.addEventListener("beforeprint", () => {
		document.documentElement.dataset["theme"] = "notion";
		document.documentElement.style.colorScheme = "light";
	});
	window.addEventListener("afterprint", apply);
}

export function setThemeChoice(next: ThemeChoice) {
	choice = next;
	try {
		if (next === "system") localStorage.removeItem(KEY);
		else localStorage.setItem(KEY, next);
	} catch {
		/* memory only */
	}
	// Ease the switch: surfaces fade for a moment instead of snapping (styles.css).
	const root = document.documentElement;
	root.classList.add("wn-theme-switching");
	apply();
	window.setTimeout(() => root.classList.remove("wn-theme-switching"), 250);
}

/** Flip between light and dark (an explicit choice from then on). */
export function toggleTheme() {
	setThemeChoice(resolve(choice) === "dark" ? "light" : "dark");
}

const subscribe = (l: () => void) => {
	listeners.add(l);
	return () => listeners.delete(l);
};

/** The stored choice and what it resolves to now. */
export function useTheme(): { choice: ThemeChoice; theme: Theme } {
	const c = useSyncExternalStore(subscribe, () => choice);
	const theme = useSyncExternalStore(subscribe, () => resolve(choice));
	return { choice: c, theme };
}

apply();
